/**
 * Import JavaScript Assets
 * @since 3.0.0
 */
import { Cookies } from "commonAssets/cookies";
import { ErrorHandler } from "commonAssets/utils/ErrorHandler";
import { Header } from "commonAssets/components/Header";
import { Navigation } from "commonAssets/components/Navigation";
import { ImportantDates } from "commonAssets/components/ImportantDates";
import { Page } from "commonAssets/pages/Page";
import { siteTitle, loadingGif, createElement } from "commonAssets/js/helpers";
import { I18n } from 'commonAssets/i18n';
import {
    loading, appDocketAPI, appClaimForm, siteInfoLang, userLanguage,
    setSiteInfo, setSiteInfoLang, setEmailInform, setPhoneAdmin,
    setEmailConfirm, setUserLanguage, setAppLocale, setI18n, outputDiv
} from "commonAssets/js/constants";
// languages
import languages from './languages';

import './style/custom.css';
import { customInitClaim, customSubmitClaim } from "./scripts/customSubmitClaim";
import { createFooter } from "./scripts/footer";

/**
 * Internationalization (i18n) Handling
 * 
 * Sets the langauges available for the distribution.
 * 
 * Examples:
 * i18n.languages returns languages in an array
 * i18n.userLanguage returns current languages
 * i18n.getTranslation('string') returns predefined string
 */
const i18n = I18n.create(['en', 'es'], {}, languages);
setI18n(() => i18n);

/**
 * Cookies
 * @returns {void}
 */
const set_cookies = new Cookies();
set_cookies.init();

/**
 * Set the user's language using the value from `set_cookies.language()`.
 * @param {Function} setCookiesLanguage - The function that returns the user's language.
 */
setUserLanguage(() => set_cookies.language());

/**
 * Set the application locale using the value from `set_cookies.locale()`.
 * @param {Function} setCookiesLocale - The function that returns the application locale.
 */
setAppLocale(() => set_cookies.locale());

/**
 * Loading GIF
 */
loadingGif(loading, "show");

/**
 * Initialize the analytics by loading the Google Tag Manager script and 
 * configuring the dataLayer and gtag. 
 */
export function analytics() {
    $.getScript("https://www.googletagmanager.com/gtag/js?id=G-LGF2TN8VN9", () => {
        window.dataLayer = window.dataLayer || [];
        function gtag() { dataLayer.push(arguments); }
        gtag('js', new Date());

        gtag('config', 'G-WKXC7VDFFT'); // swatjs
        gtag('config', 'G-LGF2TN8VN9'); // general
        gtag('config', 'G-Z5VQCWX177'); // site specific

        // Add additional `gtag`s here.
    });

    // Add custom pixels/analytics code here.
}

/**
 * Calls the appropriate function to display page content based on the given domainSlug.
 * If domainSlug is empty, the 'home' case will be executed.
 *
 * @param {Array} sitePages - An array containing the website sitePages' information.
 * @param {string} domainSlug - The unique identifier (slug) of the page to be displayed.
 * @param {} importantDates -
 *
 * @example
 * // Assuming sitePages array and domainSlug are defined and valid
 * getPage(sitePages, domainSlug, importantDates);
 */
export function getPage(sitePages, domainSlug, importantDates) {

    loadingGif(loading, "show");
    // Set domainSlug to 'home' if empty
    (domainSlug === '') ? siteTitle(sitePages) : siteTitle(sitePages, domainSlug);

    // Misc handling.
    domainSlug = domainSlug || 'home';
    const page = new Page(sitePages, domainSlug, importantDates);

    switch (domainSlug) {

        case 'home':
            page.home();
            analytics();
            break;

        case 'submit-claim':
            const submit_claim = new customSubmitClaim(appClaimForm, userLanguage, customInitClaim);
            submit_claim.form();
            analytics();
            break;

        case 'important-documents':
            page.important_documents();
            analytics();
            break;

        case 'faqs':
            page.faqs();
            analytics();
            break;

        case 'contact-us':
            // page.contact(makeStandardContactPage, userLanguage);
            page.contact();
            analytics();
            break;

        default:
            page.error();
            analytics();
    }
}

/**
 * Fetches domain API data and updates the website with the retrieved data.
 * Updates site title, site name, case number, court name, and builds navigation
 * based on the fetched data. Also adds event listener for hash changes to
 * update page content accordingly.
 *
 * @example
 * // Assuming that appDocketAPI and appLang are defined and valid
 * fetch(appDocketAPI)
 * .then(response => response.json())
 * .then(data => { ... })
 * .catch(error => console.error(error));
 */

fetch(appDocketAPI)
    .then(response => {
        if (response.status > 299) {
            // Hide loading gif
            loadingGif(loading, "hide", 1000);
            // Display error message
            const error = new ErrorHandler();
            error.display(response);
        }
        return response.json()
    })
    .then(data => {
        setSiteInfoLang(() => data.findResult.lang[userLanguage]);
        setSiteInfo(() => data.findResult);
        setEmailInform(() => data.findResult.email_info);
        setPhoneAdmin(() => data.findResult.phone);
        setEmailConfirm(() => data.findResult.email_conf);

        document.title = siteInfoLang.SiteTitle;

        // Returns the first value after FQDN. Example: fqdn.com/THIS/foo/bar
        let domainSlug = window.location.pathname.split("/")[1];

        let sitePages = siteInfoLang.Pages;

        // remove register page without visiting the cms
        sitePages = sitePages.filter(page => page.PageTitle !== "Register");

        const header = new Header(siteInfoLang.SiteName, siteInfoLang.CaseNumber, siteInfoLang.CourtName);
        const navigation = new Navigation(sitePages, domainSlug, getPage);
        const footer = createFooter();
        const importantDates = new ImportantDates(data);
        const headerElem = document.querySelector('header');
        headerElem.classList.add("py-3");
        headerElem.appendChild(header.createHeader());

        navigation.pathChange(sitePages, domainSlug, importantDates);

        document.querySelector("header").insertAdjacentHTML('afterend', navigation.element.outerHTML);
        document.querySelector("footer").appendChild(footer);

        getPage(sitePages, domainSlug, importantDates);
    })
    .catch(error => console.error(error));